import {
  Alert,
  Box,
  Button,
  Checkbox,
  FilterOptionsState,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';
import { CenteredFormPage } from 'components/CenteredFormPage';
import { AutocompleteOption, ComboField } from 'components/shared/ComboField';
import { Source } from 'dtos/users/common';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import { KeyboardEvent as ReactKeyboardEvent, useCallback } from 'react';
import { useRootStore } from 'stores/RootStore';
import { featureFlags } from 'utils/config';
import { userSettings } from 'utils/labels';
import { jobRoleOptions } from 'utils/options';

const noFilter = createFilterOptions<AutocompleteOption>();
const limitFilter = createFilterOptions<AutocompleteOption>({ limit: 20 });
const lengthAndLimitFilter = (
  options: AutocompleteOption[],
  state: FilterOptionsState<AutocompleteOption>
): AutocompleteOption[] => {
  return state.inputValue.length >= 4 ? limitFilter(options, state) : [];
};

const isOlderThanTwoDays = (dateString?: string): boolean => {
  if (!dateString) {
    return false;
  }

  const dateToCompare = DateTime.fromISO(dateString);
  return dateToCompare.isValid && dateToCompare < DateTime.now().minus({ days: 2 });
};

const CompleteProfilePage = observer(() => {
  const { completeProfileStore, authStore } = useRootStore();
  const { currentUser, getFeatureFlagValueForUser } = authStore;
  const { form, message, completeProfile, status, accounts } = completeProfileStore;

  const hasCostarLicenseField = form.$('hasCostarLicense');
  const hasCostarLicense = hasCostarLicenseField.value;

  const showNewClientTech = getFeatureFlagValueForUser(featureFlags.showNewClientTech);

  const handleCostarLicenseChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const value = evt.target.checked;
      hasCostarLicenseField.set(value);
    },
    [hasCostarLicenseField]
  );

  if (!accounts) return null;

  const handleKeyInField = (evt: ReactKeyboardEvent) => {
    if (evt.key === 'Enter') {
      evt.stopPropagation();
      evt.preventDefault();
      if (form.isValid) {
        completeProfile();
      }
    }
  };

  const isNewAccount = !isOlderThanTwoDays(currentUser?.created);

  return (
    <CenteredFormPage
      aria-label="complete profile page"
      belowHeader={
        <Box sx={{ marginTop: 4, display: 'flex', justifyContent: 'center' }}>
          <Alert sx={{ width: '396px' }} aria-label="account-created-message" severity="success">
            {isNewAccount
              ? 'Your account has been successfully created.'
              : 'Looks like your profile is incomplete! Please update it to continue logging in.'}
          </Alert>
        </Box>
      }
    >
      <form autoComplete="off">
        <Stack spacing={4} sx={{ margin: '32px 0' }}>
          <Stack spacing={2}>
            <Typography variant="h5" textAlign="center">
              <strong>Complete your Profile</strong>
            </Typography>
            <Typography variant="body1">Please tell us a bit more about you and your company.</Typography>
            {message && (
              <Alert aria-label="form-error" severity={status === 'error' ? 'error' : 'success'}>
                {message}
              </Alert>
            )}
          </Stack>
          <Stack spacing={2}>
            <TextField
              variant="outlined"
              autoComplete="off"
              onKeyDown={handleKeyInField}
              data-testid="fullName"
              {...form.$('fullName').bind()}
              required
              inputProps={{
                role: 'textbox',
              }}
            />
            {currentUser?.source !== Source.LOCAL && (
              <TextField
                variant="outlined"
                autoComplete="off"
                onKeyDown={handleKeyInField}
                data-testid="email"
                required
                {...form.$('email').bind()}
              />
            )}
            <ComboField
              field={form.$('jobRole')}
              options={jobRoleOptions}
              AutoCompleteProps={{ openOnFocus: true, selectOnFocus: true }}
              TextFieldProps={{
                'data-testid': 'jobRole',
                required: true,
              }}
              filter={noFilter}
            />
            <ComboField
              field={form.$('companyName')}
              options={accounts}
              TextFieldProps={{
                'data-testid': 'companyName',
                required: true,
              }}
              filter={lengthAndLimitFilter}
            />
            {showNewClientTech && (
              <FormControlLabel
                control={
                  <Checkbox
                    data-testid="hasCostarLicense"
                    checked={hasCostarLicense}
                    onChange={handleCostarLicenseChange}
                  />
                }
                label={userSettings.hasCostarLicense}
              />
            )}
          </Stack>
          <Button
            variant="contained"
            disableElevation
            disabled={!form.isValid}
            style={{ height: 56 }}
            onClick={() => completeProfile()}
          >
            Complete Profile and Access Portal
          </Button>
        </Stack>
      </form>
    </CenteredFormPage>
  );
});

export { CompleteProfilePage };
